import { Link } from '@inertiajs/react';
import type { VoteEdition } from '@/types';

// The 26 editions close with the sponsor wall; the 25 one with the two logos it had.
const SPONSOR_LOGOS: Array<{ name: string; src: string; widthClass: string }> = [
  {
    name: 'Anthropic',
    src: '/assets/logos/anthropic.svg',
    widthClass: 'w-24 sm:w-28 md:w-32',
  },
  {
    name: 'Profound',
    src: '/assets/logos/profound.svg',
    widthClass: 'w-20 sm:w-24 md:w-28',
  },
  {
    name: 'Supabase',
    src: '/assets/logos/supabase.svg',
    widthClass: 'w-20 sm:w-24 md:w-28',
  },
  {
    name: 'Vercel',
    src: '/assets/logos/vercel.svg',
    widthClass: 'w-16 sm:w-20 md:w-24',
  },
  {
    name: 'ElevenLabs',
    src: '/assets/logos/elevenlabs.svg',
    widthClass: 'w-20 sm:w-24 md:w-28',
  },
  {
    name: 'Kapso',
    src: '/assets/logos/kapso-padded.svg',
    widthClass: 'w-16 sm:w-20 md:w-24',
  },
  {
    name: 'Kairos',
    src: '/assets/logos/kairos.png',
    widthClass: 'w-16 sm:w-20 md:w-24',
  },
];

function maskStyle(src: string): React.CSSProperties {
  return {
    maskImage: `url(${src})`,
    maskSize: 'contain',
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    WebkitMaskImage: `url(${src})`,
    WebkitMaskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
  };
}

export function VoteFooter({ edition }: { edition: VoteEdition }) {
  if (edition.variant === '25') {
    return (
      <div className="border-primary/20 border-t bg-background">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-6 sm:px-6 lg:px-8">
          {/* Left - Platanus Hack [25] link */}
          <Link
            href="/25"
            className="font-logo text-lg text-primary lowercase tracking-tighter transition-colors hover:text-primary/80 sm:text-xl md:text-2xl"
          >
            <span className="font-light">platanus hack</span>{' '}
            <span className="font-medium">[25]</span>
          </Link>

          {/* Right - Logos */}
          <div className="flex items-center gap-3 sm:gap-4 md:gap-6">
            <a
              href="https://platan.us"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
            >
              <div
                className="aspect-[576/112] h-6 w-auto sm:h-7 md:h-8"
                style={{
                  backgroundColor: 'hsl(var(--primary))',
                  ...maskStyle('/assets/logos/platanus.svg'),
                }}
              />
            </a>
            <a
              href="https://buk.cl"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
            >
              <div
                className="aspect-[1124/424] h-5 w-auto sm:h-6 md:h-7"
                style={{
                  backgroundColor: 'hsl(var(--primary))',
                  ...maskStyle('/assets/logos/buk-crop.webp'),
                }}
              />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-primary/20 border-t bg-background">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <Link
          href={edition.homePath}
          className="flex items-center gap-3 transition-opacity hover:opacity-80"
        >
          <span className="font-logo text-lg text-primary lowercase tracking-tighter sm:text-xl md:text-2xl">
            <span className="font-light">platanus hack</span>{' '}
            <span className="font-medium">[26]</span>
          </span>
          <span
            className="font-sans text-primary text-sm uppercase sm:text-base md:text-lg"
            style={{ letterSpacing: '0.2em' }}
          >
            {edition.city}
          </span>
        </Link>

        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 md:gap-6">
          {SPONSOR_LOGOS.map((logo) => (
            <div
              key={logo.name}
              role="img"
              aria-label={logo.name}
              className={`h-6 bg-primary sm:h-7 md:h-8 ${logo.widthClass}`}
              style={maskStyle(logo.src)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
